import React, { useEffect, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import {
  archiveKycPrimaryWallet,
  getLinkedKycSubWallets,
  isLinkedSubWalletConflict,
  linkedSubWalletsFromConflict,
} from '../../apis/checkin.api';
import { apiErrorMessage } from '../../utils/apiError';
import {
  KYC_ARCHIVE_DIALOG_TITLE,
  KYC_ARCHIVE_REASON,
  KYC_UNLINK_CHECKLIST,
  kycArchiveConfirmLine,
} from '../../utils/constants';

const UNCHECKED_MESSAGE =
  'Could not load linked wallets. Archive stays off until they can be checked.';
const UNLISTED_CONFLICT_MESSAGE =
  'This primary still has linked sub-wallets. Archive stays off until they can be listed.';

// The server checks this id against the live row. The row `id` is a different
// value, so sending it always fails with PROFILE_MISMATCH. Without a real
// kycProfileId we block instead of guessing.
const MISSING_PROFILE_ID_MESSAGE =
  'This profile has no KYC profile id in the list, so it cannot be archived from here. Refresh the list; if it is still missing, ask engineering.';

const UnlinkChecklist = ({ subWallets }) => (
  <div style={{ userSelect: 'text' }}>
    <p className="mb-2">{KYC_UNLINK_CHECKLIST.lead}</p>
    <ol className="mb-0 ps-3">
      <li className="mb-2">{KYC_UNLINK_CHECKLIST.signIn}</li>
      <li className="mb-2">
        <div>{KYC_UNLINK_CHECKLIST.remove}</div>
        <div className="mt-2">
          {subWallets.map(address => (
            <div key={address} className="text-break" style={{ userSelect: 'text' }}>
              {address}
            </div>
          ))}
        </div>
      </li>
      <li>{KYC_UNLINK_CHECKLIST.recheck}</li>
    </ol>
  </div>
);

// Archive stays disabled unless the linked-wallet check returned an empty
// list. A failed check, a 401, or a 409 that still names sub-wallets all
// leave it disabled.
const ArchivePrimaryWalletModal = ({ show, profile, authToken, onHide, onArchived }) => {
  const [phase, setPhase] = useState('loading');
  const [subWallets, setSubWallets] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkNonce, setCheckNonce] = useState(0);

  useEffect(() => {
    if (!show || !profile?.walletId) return undefined;
    if (!profile.kycProfileId) {
      setSubWallets([]);
      setShowInstructions(false);
      setBusy(false);
      setPhase('blocked');
      setErrorMessage(MISSING_PROFILE_ID_MESSAGE);
      return undefined;
    }
    let cancelled = false;
    setPhase('loading');
    setSubWallets([]);
    setErrorMessage(null);
    setShowInstructions(false);
    setBusy(false);

    getLinkedKycSubWallets(profile.walletId, authToken)
      .then(wallets => {
        if (cancelled) return;
        setSubWallets(wallets);
        if (wallets.length) {
          setPhase('linked');
          setShowInstructions(true);
          return;
        }
        setPhase('confirm');
      })
      .catch(error => {
        if (cancelled) return;
        setSubWallets([]);
        setPhase('unchecked');
        setShowInstructions(false);
        setErrorMessage(apiErrorMessage(error, UNCHECKED_MESSAGE));
      });

    return () => {
      cancelled = true;
    };
  }, [show, profile, authToken, checkNonce]);

  const applyLinkedConflict = async error => {
    let wallets = linkedSubWalletsFromConflict(error, profile.walletId);
    if (!wallets.length) {
      try {
        wallets = await getLinkedKycSubWallets(profile.walletId, authToken);
      } catch {
        wallets = [];
      }
    }
    setSubWallets(wallets);
    setShowInstructions(true);
    if (wallets.length) {
      setPhase('linked');
      setErrorMessage(null);
      return;
    }
    setPhase('unchecked');
    setErrorMessage(apiErrorMessage(error, UNLISTED_CONFLICT_MESSAGE));
  };

  const confirm = async () => {
    if (phase !== 'confirm' || !profile?.kycProfileId || busy) return;
    setBusy(true);
    setErrorMessage(null);
    try {
      await archiveKycPrimaryWallet(
        profile.walletId,
        {
          expectedKycProfileId: profile.kycProfileId,
          reason: KYC_ARCHIVE_REASON,
        },
        authToken
      );
      onArchived(profile);
    } catch (error) {
      if (isLinkedSubWalletConflict(error)) {
        await applyLinkedConflict(error);
      } else {
        setErrorMessage(apiErrorMessage(error, 'Archive failed. The profile was not archived.'));
      }
      setBusy(false);
    }
  };

  const walletId = profile?.walletId ?? '';
  const archiveDisabled = phase !== 'confirm' || busy;

  return (
    <Modal
      show={show}
      onHide={busy ? undefined : onHide}
      backdrop={busy ? 'static' : true}
      centered
      aria-labelledby="archive-primary-wallet-title"
    >
      <Modal.Header closeButton={!busy}>
        <Modal.Title id="archive-primary-wallet-title">{KYC_ARCHIVE_DIALOG_TITLE}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {phase === 'loading' && (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" role="status">
              <span className="visually-hidden">Checking linked wallets</span>
            </Spinner>
            Checking linked wallets…
          </div>
        )}

        {phase === 'confirm' && <p className="mb-0">{kycArchiveConfirmLine(walletId)}</p>}

        {showInstructions && <UnlinkChecklist subWallets={subWallets} />}

        {errorMessage && (
          <Alert variant="danger" className="mt-3 mb-0">
            {errorMessage}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {(phase === 'linked' || phase === 'unchecked') && (
          <Button
            variant="outline-primary"
            className="me-auto"
            disabled={busy || phase === 'loading'}
            onClick={() => setCheckNonce(nonce => nonce + 1)}
          >
            Re-check
          </Button>
        )}
        <Button variant="secondary" disabled={busy} onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="warning"
          className="kyc-archive-confirm"
          disabled={archiveDisabled}
          onClick={confirm}
        >
          {busy ? 'Archiving…' : 'Archive'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ArchivePrimaryWalletModal;
